import { useState, useEffect, useRef } from "react";
import { Upload, CheckCircle, AlertCircle, X, Dumbbell, Key, Eye, EyeOff } from "lucide-react";
import { applyBrandingCSS } from "../../hooks/useCompanyBranding";

const API = process.env.REACT_APP_BACKEND_URL;

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl text-sm font-semibold shadow-2xl
      ${type === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
      {type === "success" ? <CheckCircle size={16} className="text-[#C9A84C]" /> : <AlertCircle size={16} className="text-red-400" />}
      {msg}
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100"><X size={14} /></button>
    </div>
  );
}

export default function InstellingenTab({ token, companyInfo, onUpdate }) {
  const b = companyInfo?.branding || {};
  const [naam, setNaam] = useState(companyInfo?.naam || "");
  const [logoUrl, setLogoUrl] = useState(b.logo_url || "");
  const [logoUploading, setLogoUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  const [bgColor,    setBgColor]    = useState(b.bg_color    || "#080808");
  const [navColor,   setNavColor]   = useState(b.nav_color   || "#0a0a0a");
  const [cardColor,  setCardColor]  = useState(b.card_color  || "#0f0f0f");
  const [card2Color, setCard2Color] = useState(b.card2_color || "#1a1a1a");
  const [borderColor,setBorderColor]= useState(b.border_color|| "#1f1f1f");
  const [primaryColor,  setPrimaryColor]  = useState(b.primary_color  || "#C9A84C");
  const [textColor,     setTextColor]     = useState(b.text_color     || "#ffffff");
  const [accentColor,   setAccentColor]   = useState(b.accent_color   || "#ffffff");
  const [vrijeTrainingEnabled, setVrijeTrainingEnabled] = useState(companyInfo?.vrije_training_enabled ?? true);

  // Mollie key state
  const [mollieKey, setMollieKey] = useState("");
  const [mollieStatus, setMollieStatus] = useState(null); // { heeft_sleutel, gemaskeerd }
  const [mollieSaving, setMollieSaving] = useState(false);
  const [showMollieKey, setShowMollieKey] = useState(false);

  const showToast = (msg, type = "success") => setToast({ msg, type });

  useEffect(() => {
    if (!companyInfo) return;
    const br = companyInfo.branding || {};
    if (companyInfo.naam) setNaam(companyInfo.naam);
    setLogoUrl(br.logo_url || "");
    setBgColor(br.bg_color       || "#080808");
    setNavColor(br.nav_color     || "#0a0a0a");
    setCardColor(br.card_color   || "#0f0f0f");
    setCard2Color(br.card2_color || "#1a1a1a");
    setBorderColor(br.border_color || "#1f1f1f");
    setPrimaryColor(br.primary_color || "#C9A84C");
    setTextColor(br.text_color   || "#ffffff");
    setAccentColor(br.accent_color || "#ffffff");
    setVrijeTrainingEnabled(companyInfo.vrije_training_enabled ?? true);
  }, [companyInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load Mollie key status
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/company/mollie-key`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setMollieStatus(d); })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    applyBrandingCSS({
      bg_color: bgColor, nav_color: navColor, card_color: cardColor,
      card2_color: card2Color, border_color: borderColor,
      primary_color: primaryColor, text_color: textColor, accent_color: accentColor,
      logo_url: logoUrl,
    });
  }, [bgColor, navColor, cardColor, card2Color, borderColor, primaryColor, textColor, accentColor, logoUrl]);

  const resizeLogo = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Bestand lezen mislukt"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Afbeelding laden mislukt"));
      img.onload = () => {
        const MAX = 300;
        let { width: w, height: h } = img;
        if (w > h) { if (w > MAX) { h = Math.round(h * MAX / w); w = MAX; } }
        else        { if (h > MAX) { w = Math.round(w * MAX / h); h = MAX; } }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { showToast("Bestand te groot (max 5 MB)", "error"); return; }
    setLogoUploading(true);
    try {
      const dataUrl = await resizeLogo(file);
      setLogoUrl(dataUrl);
    } catch (err) { showToast(err.message || "Upload mislukt", "error"); }
    finally { setLogoUploading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/company/me/branding`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          naam,
          primary_color: primaryColor,
          accent_color: accentColor,
          text_color: textColor,
          logo_url: logoUrl,
          bg_color: bgColor,
          nav_color: navColor,
          card_color: cardColor,
          card2_color: card2Color,
          border_color: borderColor,
          vrije_training_enabled: vrijeTrainingEnabled,
        }),
      });
      if (!r.ok) throw new Error("Opslaan mislukt");
      showToast("Instellingen opgeslagen!");
      onUpdate();
    } catch (err) { showToast(err.message, "error"); }
    finally { setLoading(false); }
  };

  const KLEUR_GROEPEN = [
    {
      titel: "Achtergronden",
      sub: "Pas de achtergrondkleuren van elke laag aan",
      items: [
        { label: "Hoofdachtergrond",       hint: "De pagina-achtergrond",               val: bgColor,     set: setBgColor,     id: "bg-color"     },
        { label: "Navigatiebalk",           hint: "Boven- en zijbalk",                  val: navColor,    set: setNavColor,    id: "nav-color"    },
        { label: "Kaart / paneel",          hint: "Kaarten en panels",                  val: cardColor,   set: setCardColor,   id: "card-color"   },
        { label: "Invoervelden & genest",   hint: "Inputs, dropdowns, binnenste lagen", val: card2Color,  set: setCard2Color,  id: "card2-color"  },
      ],
    },
    {
      titel: "Rantjes & tekst",
      sub: "Randkleuren en tekstkleur voor alle elementen",
      items: [
        { label: "Randkleur (rantjes)",  hint: "Borders overal in het dashboard", val: borderColor,  set: setBorderColor,  id: "border-color"  },
        { label: "Primaire tekst",       hint: "Koppen en waarden",               val: textColor,    set: setTextColor,    id: "text-color"    },
      ],
    },
    {
      titel: "Knoppen & accenten",
      sub: "Primaire kleur voor knoppen, actieve tabs en highlights",
      items: [
        { label: "Knop & accentkleur",  hint: "Actieve knoppen en tabs",  val: primaryColor, set: setPrimaryColor, id: "primary-color" },
        { label: "Accent (details)",    hint: "Kleine visuele accenten",   val: accentColor,  set: setAccentColor,  id: "accent-color"  },
      ],
    },
  ];

  const isPro = companyInfo?.plan === "pro" || companyInfo?.plan === "business";

  return (
    <div className="max-w-lg">
      <div className="mb-6">
        <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Configuratie</p>
        <h2 className="text-xl font-bold text-white">Whitelabel Instellingen</h2>
        <p className="text-zinc-500 text-sm mt-1">Wijzigingen worden direct zichtbaar in het dashboard.</p>
      </div>

      <form onSubmit={handleSave} className="space-y-5">
        {/* Bedrijfsnaam */}
        <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-5">
          <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">Bedrijfsnaam</p>
          <input
            value={naam}
            onChange={e => setNaam(e.target.value)}
            placeholder="Naam van jouw bedrijf"
            className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
            data-testid="bedrijfsnaam-input"
          />
        </div>
        {/* Logo & Kleuren — alleen voor PRO en BUSINESS */}
        {!isPro ? (
          <div className="bg-[#0f0f0f] border border-[#C9A84C]/20 rounded-2xl p-6 text-center">
            <div className="w-12 h-12 rounded-2xl bg-[#C9A84C]/10 flex items-center justify-center mx-auto mb-3">
              <Dumbbell size={22} className="text-[#C9A84C]" />
            </div>
            <p className="text-white font-bold mb-1">Eigen branding — PRO & BUSINESS</p>
            <p className="text-zinc-500 text-sm mb-4">Logo, kleuren en volledig whitelabel zijn beschikbaar vanaf het PRO-pakket (€99/4w).</p>
            <a href="/bedrijf-admin/abonnement"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-black transition-all hover:opacity-90"
              style={{ background: "#C9A84C" }}>
              Upgraden naar PRO
            </a>
          </div>
        ) : (
          <>
            {/* Logo upload */}
            <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-5">
          <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-4">Bedrijfslogo</p>
          <div className="flex items-center gap-4">
            <div
              className="w-20 h-20 rounded-2xl border-2 border-dashed border-white/15 flex items-center justify-center shrink-0 overflow-hidden cursor-pointer transition-all hover:border-[#C9A84C]/40 bg-white/3"
              onClick={() => fileInputRef.current?.click()}
              data-testid="logo-upload-preview"
            >
              {logoUploading ? (
                <div className="w-5 h-5 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
              ) : logoUrl ? (
                <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center">
                  <Upload size={20} className="text-zinc-600 mx-auto mb-1" />
                  <span className="text-zinc-600 text-[10px]">Upload</span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <p className="text-white text-sm font-semibold mb-1">{logoUrl ? "Logo ingesteld" : "Geen logo"}</p>
              <p className="text-zinc-500 text-xs mb-3">PNG, JPG of SVG · max 5 MB · wordt automatisch verkleind naar 300×300 px</p>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  data-testid="logo-upload-btn"
                  className="px-4 py-2 text-xs font-bold uppercase tracking-wide rounded-xl border border-white/15 text-zinc-300 hover:border-white/30 hover:text-white transition-all">
                  {logoUrl ? "Wijzigen" : "Foto kiezen"}
                </button>
                {logoUrl && (
                  <button type="button"
                    onClick={() => { setLogoUrl(""); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                    data-testid="logo-remove-btn"
                    className="px-4 py-2 text-xs font-bold uppercase tracking-wide rounded-xl border border-red-500/20 text-red-400 hover:bg-red-500/8 transition-all">
                    Verwijderen
                  </button>
                )}
              </div>
            </div>
          </div>
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
            onChange={handleLogoFile} className="hidden" data-testid="logo-file-input" />
        </div>

        {/* Kleurengroepen */}
        {KLEUR_GROEPEN.map(({ titel, sub, items }) => (
          <div key={titel} className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-5">
            <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-0.5">{titel}</p>
            <p className="text-zinc-600 text-xs mb-4">{sub}</p>
            <div className="space-y-3">
              {items.map(({ label, hint, val, set, id }) => (
                <div key={id} className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <input
                      type="color"
                      value={val}
                      onChange={e => set(e.target.value)}
                      className="w-11 h-11 rounded-xl cursor-pointer border-0 p-0.5 bg-transparent"
                      style={{ accentColor: val }}
                      data-testid={id}
                    />
                    <div
                      className="absolute inset-0.5 rounded-[10px] pointer-events-none border border-white/10"
                      style={{ background: val }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium leading-tight">{label}</p>
                    <p className="text-zinc-600 text-xs">{hint}</p>
                  </div>
                  <span className="text-zinc-500 text-xs font-mono flex-shrink-0">{val}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
          </>
        )}

        {/* Vrije Training schakelaar */}
        <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-5">
          <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1">Functies</p>
          <p className="text-zinc-600 text-xs mb-4">Beheer welke functies beschikbaar zijn voor jouw sporters</p>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${vrijeTrainingEnabled ? "bg-[#C9A84C]/15" : "bg-white/5"}`}>
                <Dumbbell size={16} className={vrijeTrainingEnabled ? "text-[#C9A84C]" : "text-zinc-600"} />
              </div>
              <div>
                <p className="text-white text-sm font-semibold">Vrije Training</p>
                <p className="text-zinc-500 text-xs">Sporters kunnen zelf trainingen samenstellen</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setVrijeTrainingEnabled(v => !v)}
              data-testid="vrije-training-toggle"
              className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${vrijeTrainingEnabled ? "bg-[#C9A84C]" : "bg-zinc-700"}`}
              aria-label="Vrije Training in-/uitschakelen"
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${vrijeTrainingEnabled ? "translate-x-6" : "translate-x-0.5"}`} />
            </button>
          </div>
          {!vrijeTrainingEnabled && (
            <p className="mt-3 text-amber-400/80 text-xs bg-amber-400/8 border border-amber-400/20 rounded-xl px-3 py-2">
              Vrije Training is uitgeschakeld. Sporters zien een vergrendeld scherm en kunnen geen vrije trainingen starten.
            </p>
          )}
        </div>

        {/* Mollie configuratie */}
        <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-[#C9A84C]/10 flex items-center justify-center">
              <Key size={16} className="text-[#C9A84C]" />
            </div>
            <div>
              <p className="text-white text-sm font-semibold">Mollie betaalkoppeling</p>
              <p className="text-zinc-500 text-xs">Koppel je eigen Mollie-account voor creditbetalingen</p>
            </div>
          </div>
          {mollieStatus?.heeft_sleutel && (
            <div className="flex items-center gap-2 mb-3 bg-green-500/8 border border-green-500/20 rounded-xl px-3 py-2">
              <CheckCircle size={13} className="text-green-400 shrink-0" />
              <p className="text-green-300 text-xs font-semibold">Actieve sleutel: <span className="font-mono">{mollieStatus.gemaskeerd}</span></p>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block">
                {mollieStatus?.heeft_sleutel ? "Nieuwe API-sleutel (overschrijft huidige)" : "Mollie API-sleutel"}
              </label>
              <div className="relative">
                <input
                  type={showMollieKey ? "text" : "password"}
                  value={mollieKey}
                  onChange={e => setMollieKey(e.target.value)}
                  placeholder="live_... of test_..."
                  data-testid="mollie-key-input"
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm pr-10 focus:outline-none focus:border-[#C9A84C]/50 placeholder-zinc-600 font-mono"
                />
                <button type="button" onClick={() => setShowMollieKey(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                  {showMollieKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <p className="text-zinc-600 text-xs mt-1.5">
                Vind je sleutel op{" "}
                <a href="https://my.mollie.com/dashboard/developers/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:underline">
                  my.mollie.com → API-sleutels
                </a>
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={mollieSaving || !mollieKey.trim()}
                data-testid="save-mollie-key-btn"
                onClick={async () => {
                  setMollieSaving(true);
                  try {
                    const r = await fetch(`${API}/api/company/mollie-key`, {
                      method: "PUT",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ mollie_api_key: mollieKey.trim() }),
                    });
                    const d = await r.json();
                    if (!r.ok) throw new Error(d.detail || "Opslaan mislukt");
                    setMollieStatus({ heeft_sleutel: true, gemaskeerd: mollieKey.slice(0, 8) + "..." + mollieKey.slice(-4) });
                    setMollieKey("");
                    showToast("Mollie-sleutel opgeslagen!");
                  } catch (err) { showToast(err.message, "error"); }
                  finally { setMollieSaving(false); }
                }}
                className="px-4 py-2.5 rounded-xl text-black text-xs font-bold disabled:opacity-40"
                style={{ background: "#C9A84C" }}>
                {mollieSaving ? "Opslaan..." : "Sleutel opslaan"}
              </button>
              {mollieStatus?.heeft_sleutel && (
                <button
                  type="button"
                  data-testid="remove-mollie-key-btn"
                  onClick={async () => {
                    if (!window.confirm("Mollie-sleutel verwijderen?")) return;
                    try {
                      await fetch(`${API}/api/company/mollie-key`, {
                        method: "PUT",
                        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ mollie_api_key: "" }),
                      });
                      setMollieStatus({ heeft_sleutel: false, gemaskeerd: "" });
                      showToast("Mollie-sleutel verwijderd");
                    } catch {}
                  }}
                  className="px-4 py-2.5 rounded-xl border border-red-500/20 text-red-400 text-xs font-semibold hover:bg-red-500/8 transition-all">
                  Verwijderen
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Live Preview */}
        <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-5">
          <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-4">Live voorbeeld</p>
          <div className="rounded-xl overflow-hidden border" style={{ borderColor: borderColor }}>
            <div className="px-4 py-2.5 flex items-center gap-2 border-b" style={{ background: navColor, borderColor: borderColor }}>
              <div className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black"
                style={{ background: primaryColor, color: navColor }}>
                {(naam || "B")[0].toUpperCase()}
              </div>
              <span className="font-bold text-sm" style={{ color: textColor }}>{naam || "Jouw Bedrijf"}</span>
            </div>
            <div className="p-4 space-y-3" style={{ background: bgColor }}>
              <div className="flex gap-1.5 p-1 rounded-lg border" style={{ background: cardColor, borderColor: borderColor }}>
                {["Dashboard", "Sporters", "Schema's"].map((t, i) => (
                  <div key={t} className="px-3 py-1.5 rounded-md text-xs font-semibold"
                    style={i === 0 ? { background: primaryColor, color: navColor } : { color: textColor + "80" }}>
                    {t}
                  </div>
                ))}
              </div>
              <div className="rounded-xl p-3 border" style={{ background: cardColor, borderColor: borderColor }}>
                <p className="text-xs font-semibold mb-2" style={{ color: textColor }}>Sporters overzicht</p>
                <div className="rounded-lg p-2.5 border" style={{ background: card2Color, borderColor: borderColor }}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: textColor }}>Demo Sporter</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: primaryColor + "22", color: primaryColor }}>Actief</span>
                  </div>
                </div>
              </div>
              <button className="w-full py-2.5 rounded-xl text-xs font-black" style={{ background: primaryColor, color: navColor }}>
                Start training
              </button>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="save-branding-btn"
          className="w-full py-3.5 text-black font-bold text-sm rounded-xl disabled:opacity-40 transition-opacity"
          style={{ background: primaryColor }}
        >
          {loading ? "Opslaan..." : "Instellingen opslaan"}
        </button>
      </form>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
